// src/components/gatherings/TimelineView.tsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Tooltip, 
  CircularProgress,
  Alert
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Import Grid directly as per MUI v7 pattern
import { format, parseISO } from 'date-fns';
import { GatheringStatus } from '../../../../types';

interface BookedSlot {
  start_time: string;
  end_time: string;
  gathering_title?: string;
  gathering_id?: string;
  status?: GatheringStatus;
}

interface AreaSchedule {
  id: string;
  name: string;
  capacity: number;
  booked_slots: BookedSlot[];
}

interface TimelineViewProps {
  date: string;
  areas: AreaSchedule[];
  onTimeSlotSelect: (startTime: string, endTime: string, areaId: string) => void;
  selectedSlot: { startTime: string; endTime: string; areaId: string } | null;
  gatheringDuration: number;
  isLoading: boolean;
  error: string | null;
  editingGatheringId?: string;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  date,
  areas,
  onTimeSlotSelect,
  selectedSlot,
  gatheringDuration,
  isLoading,
  error,
  editingGatheringId
}) => {
  // Define the hours to display (8 AM to 9 PM)
  const hours = Array.from({ length: 14 }, (_, i) => i + 8);

  // Get status color for booked slots
  const getStatusColor = (status?: GatheringStatus) => {
    switch (status) {
      case GatheringStatus.NEW:
        return { bg: 'rgba(52, 152, 219, 0.2)', border: '#3498db', text: '#3498db' }; // Blue
      case GatheringStatus.IN_REVIEW:
        return { bg: 'rgba(243, 156, 18, 0.2)', border: '#f39c12', text: '#f39c12' }; // Orange
      case GatheringStatus.NEED_MORE_INFO:
        return { bg: 'rgba(231, 76, 60, 0.2)', border: '#e74c3c', text: '#e74c3c' }; // Red
      case GatheringStatus.RESCHEDULE:
        return { bg: 'rgba(155, 89, 182, 0.2)', border: '#9b59b6', text: '#9b59b6' }; // Purple
      case GatheringStatus.AWAITING_GATHERING_LINK:
        return { bg: 'rgba(46, 204, 113, 0.2)', border: '#2ecc71', text: '#2ecc71' }; // Green
      case GatheringStatus.READY_TO_PUBLISH:
        return { bg: 'rgba(26, 188, 156, 0.2)', border: '#1abc9c', text: '#1abc9c' }; // Teal
      case GatheringStatus.PUBLISHED:
        return { bg: 'rgba(39, 174, 96, 0.2)', border: '#27ae60', text: '#27ae60' }; // Dark Green
      case GatheringStatus.DENIED:
        return { bg: 'rgba(127, 140, 141, 0.2)', border: '#7f8c8d', text: '#7f8c8d' }; // Gray
      default:
        return { bg: 'rgba(149, 165, 166, 0.2)', border: '#95a5a6', text: '#95a5a6' }; // Light Gray
    }
  };

  // Convert time string to position in the timeline
  const timeToPosition = (time: string) => {
    // Handle both "19:00:00" and "7:00 PM" formats
    if (time.includes(':')) {
      if (time.includes('AM') || time.includes('PM')) {
        // Format: "7:00 PM"
        const [timeStr, period] = time.split(' ');
        const [hour, minute] = timeStr.split(':').map(Number);
        let hour24 = hour;
        if (period === 'PM' && hour !== 12) hour24 += 12;
        if (period === 'AM' && hour === 12) hour24 = 0;
        
        const startHour = 8; // Timeline starts at 8 AM
        return ((hour24 - startHour) * 60 + minute) / 60; // Position in hours from 8 AM
      } else {
        // Format: "19:00:00"
        const [hour, minute] = time.split(':').map(Number);
        const startHour = 8; // Timeline starts at 8 AM
        return ((hour - startHour) * 60 + minute) / 60; // Position in hours from 8 AM
      }
    }
    return 0;
  };

  // Format hour for display
  const formatTime = (hour: number) => {
    if (hour === 12) return '12:00 PM';
    if (hour > 12) return `${hour - 12}:00 PM`;
    return `${hour}:00 AM`;
  };

  // Format time from hour float (e.g., 9.5 -> "9:30 AM")
  const formatTimeFromHour = (hourFloat: number) => {
    const hour = Math.floor(hourFloat);
    const minute = Math.round((hourFloat - hour) * 60);
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour >= 12 ? 'PM' : 'AM';
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  // Check if a time slot is available for a specific area
  const isSlotAvailable = (areaId: string, startHour: number, durationHours: number) => {
    const area = areas.find(a => a.id === areaId);
    if (!area) return false;

    const endHour = startHour + durationHours;
    
    // Check if this time conflicts with any booked slots
    return !area.booked_slots.some(slot => {
      // Skip checking the gathering being edited
      if (editingGatheringId && slot.gathering_id === editingGatheringId) {
        return false;
      }
      
      // Skip denied gatherings - they can be scheduled over
      if (slot.status === GatheringStatus.DENIED) {
        return false;
      }
      
      const slotStart = timeToPosition(slot.start_time) + 8;
      const slotEnd = timeToPosition(slot.end_time) + 8;
      
      return (startHour < slotEnd && endHour > slotStart);
    });
  };

  // Handle click on a time slot
  const handleTimeSlotClick = (areaId: string, hour: number) => {
    const durationHours = gatheringDuration / 60;
    if (isSlotAvailable(areaId, hour, durationHours)) {
      const startTime = formatTimeFromHour(hour);
      const endHour = hour + durationHours;
      const endTime = formatTimeFromHour(endHour);
      onTimeSlotSelect(startTime, endTime, areaId);
    }
  };

  // Get position and width for selected slot
  const getSelectedSlotPosition = (areaId: string) => {
    if (!selectedSlot || selectedSlot.areaId !== areaId) return null;
    
    const startPosition = timeToPosition(selectedSlot.startTime);
    const endPosition = timeToPosition(selectedSlot.endTime);
    const left = (startPosition / 13) * 100;
    const width = ((endPosition - startPosition) / 13) * 100;
    
    return { left, width };
  };

  // If loading or error, show appropriate UI
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
    );
  }

  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = parseISO(dateString);
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  return (
    <Box>
      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Daily Schedule - {formatDate(date)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Duration needed: {gatheringDuration} minutes ({Math.ceil(gatheringDuration / 60)} {Math.ceil(gatheringDuration / 60) === 1 ? 'hour' : 'hours'})
      </Typography>

      {/* Time header */}
      <Box sx={{ display: 'flex', mb: 2 }}>
        <Box sx={{ width: '120px', flexShrink: 0 }}></Box>
        <Box sx={{ flex: 1, display: 'flex' }}>
          {hours.map(hour => (
            <Box 
              key={hour} 
              sx={{ 
                flex: 1, 
                textAlign: 'center', 
                borderLeft: '1px solid',
                borderColor: 'divider',
                px: 1,
                fontSize: '0.75rem',
                color: 'text.secondary'
              }}
            >
              {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Area schedules */}
      <Box sx={{ mb: 4 }}>
        {areas.map(area => (
          <Box key={area.id} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ width: '120px', flexShrink: 0, pr: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{area.name}</Typography>
              <Typography variant="caption" color="text.secondary">Capacity: {area.capacity}</Typography>
            </Box>
            
            <Box sx={{ flex: 1, position: 'relative', height: '48px', bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {/* Time grid lines */}
              {hours.map(hour => (
                <Box
                  key={hour}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${((hour - 8) / 13) * 100}%`,
                    borderLeft: '1px solid',
                    borderColor: 'divider'
                  }}
                />
              ))}

              {/* Booked slots */}
              {area.booked_slots.map((slot, index) => {
                const start = timeToPosition(slot.start_time);
                const end = timeToPosition(slot.end_time);
                const width = ((end - start) / 13) * 100;
                const left = (start / 13) * 100;
                const statusColors = getStatusColor(slot.status);

                return (
                  <Tooltip
                    key={index}
                    title={
                      <Box>
                        <Typography variant="body2">{slot.gathering_title || 'Booked'}</Typography>
                        <Typography variant="caption">{slot.start_time} - {slot.end_time}</Typography>
                        {slot.status && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Status: {slot.status}
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '4px',
                        bottom: '4px',
                        left: `${left}%`,
                        width: `${width}%`,
                        bgcolor: statusColors.bg,
                        border: '1px solid',
                        borderColor: statusColors.border,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: statusColors.text,
                        fontSize: '0.75rem'
                      }}
                    >
                      {slot.gathering_title || 'Booked'}
                    </Box>
                  </Tooltip>
                );
              })}

              {/* Selected time slot visual */}
              {(() => {
                const selectedPosition = getSelectedSlotPosition(area.id);
                if (selectedPosition) {
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '4px',
                        bottom: '4px',
                        left: `${selectedPosition.left}%`,
                        width: `${selectedPosition.width}%`,
                        bgcolor: 'primary.main',
                        opacity: 0.75,
                        border: '2px solid',
                        borderColor: 'primary.dark',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      Selected
                    </Box>
                  );
                }
                return null;
              })()}

              {/* Available time slots (clickable) - 30-minute intervals from 8 AM to 9 PM */}
              {Array.from({ length: (13 * 2) }, (_, i) => 8 + i * 0.5).map(hourFloat => {
                const durationHours = gatheringDuration / 60;
                const available = isSlotAvailable(area.id, hourFloat, durationHours);
                const slotWidth = (durationHours / 13) * 100; // relative to 13 hours (8 AM - 9 PM)

                return (
                <Box
                  key={hourFloat}
                  onClick={() => handleTimeSlotClick(area.id, hourFloat)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${((hourFloat - 8) / 13) * 100}%`,
                    width: `${slotWidth}%`,
                    cursor: available ? 'pointer' : 'not-allowed',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: 'transparent',
                    '&:hover': {
                      bgcolor: available ? 'rgba(46, 204, 113, 0.1)' : 'transparent',
                      border: available ? '1px dashed' : 'none',
                      borderColor: available ? 'success.main' : 'transparent',
                      color: available ? 'success.main' : 'transparent' // text appears on hover
                    }
                  }}
                >
                  {available &&
                    `${formatTimeFromHour(hourFloat)} - ${formatTimeFromHour(hourFloat + durationHours)}`}
                </Box>

                );
              })}

            </Box>
          </Box>
        ))}
      </Box>

      {/* Legend */}
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Legend</Typography>
      <Grid container spacing={1}>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.NEW).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.NEW).border, borderRadius: '2px' }} />
            <Typography variant="caption">New</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.IN_REVIEW).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.IN_REVIEW).border, borderRadius: '2px' }} />
            <Typography variant="caption">In Review</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.NEED_MORE_INFO).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.NEED_MORE_INFO).border, borderRadius: '2px' }} />
            <Typography variant="caption">Need More Info</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.RESCHEDULE).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.RESCHEDULE).border, borderRadius: '2px' }} />
            <Typography variant="caption">Reschedule</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.DENIED).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.DENIED).border, borderRadius: '2px' }} />
            <Typography variant="caption">Denied</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.AWAITING_GATHERING_LINK).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.AWAITING_GATHERING_LINK).border, borderRadius: '2px' }} />
            <Typography variant="caption">Awaiting Link</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.READY_TO_PUBLISH).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.READY_TO_PUBLISH).border, borderRadius: '2px' }} />
            <Typography variant="caption">Ready to Publish</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: getStatusColor(GatheringStatus.PUBLISHED).bg, border: '1px solid', borderColor: getStatusColor(GatheringStatus.PUBLISHED).border, borderRadius: '2px' }} />
            <Typography variant="caption">Published</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: 'primary.main', opacity: 0.75, border: '1px solid', borderColor: 'primary.dark', borderRadius: '2px' }} />
            <Typography variant="caption">Selected</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Selected slot info */}
      {selectedSlot && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="primary.contrastText">Selected Time Slot</Typography>
          <Typography variant="body2" color="primary.contrastText">
            {areas.find(a => a.id === selectedSlot.areaId)?.name || 'Unknown Area'} - {selectedSlot.startTime} to {selectedSlot.endTime}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TimelineView;
